import { useEffect, useMemo, useState } from 'react';
import { subDays } from 'date-fns';
import { enqueueSnackbar } from 'notistack';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Chip, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { CountryBar } from 'src/components/country-bar/CountryBar';
import { CountryItem } from 'src/components/country-bar/types';
import { deriveCountryCode } from 'src/components/visibility/hooks/countryCode';
import { useRouter } from 'src/routes/hooks';
import { buildAdsDeepLink } from 'src/components/data-grid/brand-infringement/deepLink';
import { useCountriesCoverageQuery, useCountriesQuery } from 'src/context/api/filters/api';
import { dataApi } from 'src/context/api/data/api';
import getSerpErrorMessage from 'src/components/visibility/components/getSerpErrorMessage';
import {
    InfringementRow,
    useExportTrademarkReportMutation,
    useGetInfringementGridQuery,
    useLazyGetInfringementExportQuery,
} from 'src/context/api/brandInfringement/api';
import { useCurrentWorkspace } from 'src/context/reducers/app-settings';
import Iconify from 'src/components/iconify';
import { fDate } from 'src/utils/format-time';
import DataGridBrandInfringement from 'src/components/data-grid/brand-infringement/data-grid-brand-infringement';
import { buildCsv, csvFilename, downloadTextFile } from 'src/components/data-grid/brand-infringement/exportUtils';

type Days = 3 | 7 | 30;

const DAYS_OPTIONS: { value: Days; label: string }[] = [
    { value: 3, label: 'Last 3 days' },
    { value: 7, label: 'Last 7 days' },
    { value: 30, label: 'Last 30 days' },
];

// The country bar is workspace-level context, so it is deliberately NOT tied to the grid's period
// selector: re-querying coverage on every 3/7/30 switch blanks and reloads the bar for nothing. We
// pin it to the widest period on offer, which makes its list a superset of any selection and keeps
// one cache entry per workspace.
const COVERAGE_LOOKBACK_DAYS = Math.max(...DAYS_OPTIONS.map((option) => option.value));

// Figma: the selected period sits in a grey pill, like the period selector of the other Brand Shield
// grids (activations CustomToolbar).
const PERIOD_CHIP_SX = {
    height: 24,
    borderRadius: '6px',
    bgcolor: 'rgba(145, 158, 171, 0.32)',
    color: '#333C47',
    fontSize: 13,
    fontWeight: 500,
    cursor: 'pointer',
    '& .MuiChip-label': { px: '8px' },
    // The theme repaints a filled chip `grey[700]` on hover (overrides/components/chip.tsx): same
    // hover tint as the activations toolbar instead.
    '&:hover': { bgcolor: 'rgba(145, 158, 171, 0.50)' },
} as const;

// Figma period menu: a solid white list (the theme's dropdowns are blurred and see-through), no
// tinted selection, a tick on the selected option instead.
const PERIOD_MENU_PAPER_SX = {
    p: 0,
    borderRadius: '8px',
    boxShadow: '0px 2px 5px 0px rgba(0, 0, 0, 0.16)',
    bgcolor: '#FFFFFF',
    backgroundImage: 'none',
    backdropFilter: 'none',
    WebkitBackdropFilter: 'none',
} as const;

const PERIOD_OPTION_SX = {
    display: 'flex',
    justifyContent: 'space-between',
    gap: 1,
    m: 0,
    px: 2,
    py: 1,
    borderRadius: 0,
    fontSize: 14,
    fontWeight: 500,
    color: '#919EAB',
    '&:not(:last-of-type)': { mb: 0, borderBottom: '1px solid rgba(145, 158, 171, 0.12)' },
    '&.Mui-selected, &.Mui-selected:hover, &.Mui-selected.Mui-focusVisible': {
        bgcolor: 'transparent',
        color: '#333C47',
        fontWeight: 500,
    },
} as const;

export default function BrandInfringementView() {
    const router = useRouter();
    const workspace: any = useCurrentWorkspace();
    const [days, setDays] = useState<Days>(7);
    const [selectedCountry, setSelectedCountry] = useState<string | null>(null);
    const [busyDomain, setBusyDomain] = useState<string | null>(null);
    const [isExporting, setIsExporting] = useState(false);

    // Not derived from `days` on purpose — see COVERAGE_LOOKBACK_DAYS.
    const { startDate, endDate } = useMemo(() => {
        const today = new Date();
        return {
            startDate: fDate(subDays(today, COVERAGE_LOOKBACK_DAYS), 'yyyy-MM-dd'),
            endDate: fDate(today, 'yyyy-MM-dd'),
        };
    }, []);

    // Tells us whether this workspace has more than one country *before* the coverage query lands,
    // so a mono-country workspace never flashes the bar's skeleton. The header already runs this
    // exact query for every workspace (header.tsx), so with identical arguments this is a cache hit
    // rather than a second request. Preferred over `filters.loadedCountries`, which the header only
    // ever writes when non-empty and never resets — it keeps reporting the previous workspace's
    // count after a switch, whereas the cache entry is simply `undefined` while the new one loads.
    const filterStore: any = useSelector((state: any) => state.filters);
    const { data: workspaceCountries } = useCountriesQuery(
        {
            workspace: workspace?.bqTable,
            platforms: filterStore?.platforms,
            startDate: filterStore?.start,
            endDate: filterStore?.end,
        },
        { skip: !workspace?.bqTable }
    );
    const expectsCountryBar = (workspaceCountries?.countries?.length ?? 0) > 1;

    const { data: coverageData, isFetching: isLoadingCountries } = useCountriesCoverageQuery(
        {
            workspace: workspace?.bqTable,
            platforms: undefined,
            devices: undefined,
            startDate,
            endDate,
        },
        { skip: !workspace?.bqTable }
    );

    const countriesData: CountryItem[] = useMemo(() => {
        const rows: any[] = Array.isArray(coverageData?.countries) ? coverageData.countries : [];
        return rows
            .map((row: any) => ({
                country_name: row.region_name,
                country_code: deriveCountryCode(row.region_name),
                share_of_brand_searches:
                    typeof row.brand_traffic_share === 'number' ? row.brand_traffic_share : null,
            }))
            .sort((a, b) => (b.share_of_brand_searches ?? 0) - (a.share_of_brand_searches ?? 0));
    }, [coverageData]);

    // The selection must be one of this workspace's countries: the page does not remount on a
    // workspace switch, so the previous workspace's country would otherwise stick. Wait for the
    // coverage of the current workspace (RTK keeps the previous one while the new one loads).
    useEffect(() => {
        if (isLoadingCountries) return;
        if (!countriesData.some((country) => country.country_name === selectedCountry)) {
            setSelectedCountry(countriesData[0]?.country_name ?? null);
        }
    }, [selectedCountry, countriesData, isLoadingCountries]);

    // One BigQuery job per (workspace, period): the rows of every region come back at once and the
    // country bar only filters them, so switching countries costs no query.
    const { currentData, isFetching, isError, error } = useGetInfringementGridQuery(
        { workspaceTable: workspace?.bqTable, days },
        { skip: !workspace?.bqTable }
    );

    // RTK keeps the last successful response when a query fails: say so instead of showing the
    // previous selection's rows under the new one.
    useEffect(() => {
        if (isError) {
            enqueueSnackbar(getSerpErrorMessage(error, 'Brand Infringement data could not be loaded.'), {
                variant: 'error',
            });
        }
    }, [isError, error]);

    const [getExport] = useLazyGetInfringementExportQuery();
    const dispatch = useDispatch();
    const [exportTrademarkReport] = useExportTrademarkReportMutation();

    const handleExportCsv = async (domain?: string) => {
        if (isExporting) return;
        setIsExporting(true);
        try {
            const { rows: exportRows } = await getExport({
                workspaceTable: workspace?.bqTable,
                country: selectedCountry,
                days,
                domain,
            }).unwrap();
            downloadTextFile(csvFilename(workspace?.name ?? '', domain), buildCsv(exportRows), 'text/csv');
        } catch (err) {
            enqueueSnackbar(getSerpErrorMessage(err), { variant: 'error' });
        } finally {
            setIsExporting(false);
        }
    };

    // Per-domain export goes through a server-side job, downloaded like every other
    // export by the notifications popover (auto-download + toast), so nothing is
    // downloaded here. "Export all" (handleExportCsv) deliberately keeps the
    // client-side path.
    const handleDownloadDomainReport = async (row: InfringementRow) => {
        if (!workspace?.bqTable || busyDomain) return;
        setBusyDomain(row.domain_name);
        try {
            await exportTrademarkReport({
                workspaceTable: workspace?.bqTable,
                country: selectedCountry,
                days,
                domain: row.domain_name,
            }).unwrap();
            // The job is already completed: refetch the jobs now so the popover picks
            // it up without waiting for its next poll.
            dispatch(dataApi.util.invalidateTags(['ExportJobs']));
        } catch (err) {
            // This page owns the feedback for its own export (see
            // SELF_REPORTED_ACTIONS in exports.helpers): the backend message when
            // there is one, an English default otherwise — an unhandled backend
            // exception carries no `data.error`.
            enqueueSnackbar(getSerpErrorMessage(err, 'Export failed. Please try again.'), {
                variant: 'error',
            });
        } finally {
            setBusyDomain(null);
        }
    };

    const handleTermClick = (term: string, row?: InfringementRow) => {
        router.push(buildAdsDeepLink({
            countryCode: selectedCountry ? deriveCountryCode(selectedCountry) : undefined,
            domain: row?.domain_name ?? '',
            searchTerm: term,
            brandInfringement: true,
        }));
    };

    // "Click on the cell to See Ads for these Search terms": the domain's Ads with the Brand
    // Infringement filter on, i.e. its infringing ads, which are the ones on these terms.
    const handleTermsCellClick = (row: InfringementRow) => {
        router.push(buildAdsDeepLink({
            countryCode: selectedCountry ? deriveCountryCode(selectedCountry) : undefined,
            domain: row.domain_name,
            brandInfringement: true,
        }));
    };

    const rows = useMemo(
        () => (isError ? [] : (currentData?.rows ?? []).filter((row) => row.region_name === selectedCountry)),
        [isError, currentData, selectedCountry]
    );

    // Any load, first or not: the whole card stands in as one skeleton block instead of a spinner
    // over the grid, like the Brand Pulse leaderboard and the Explore table. It also covers the wait
    // for the workspace's countries; a workspace with none settles on an empty grid.
    const awaitingCountries = !!workspace?.bqTable && isLoadingCountries;
    const showTableSkeleton =
        isFetching || awaitingCountries || (!!selectedCountry && !currentData && !isError);

    return (
        <Box>
            {/* The skeleton is reserved only when we already know the workspace has several
                countries. On a mono-country workspace it never shows, so the bar no longer flashes
                in and out. */}
            <CountryBar
                countries={countriesData}
                selectedCountry={selectedCountry}
                onCountryChange={setSelectedCountry}
                loading={expectsCountryBar && isLoadingCountries}
                indicator="radio"
                flagSize={14}
                tabSpacing={16}
            />

            <Box
                px="24px"
                // Without a country bar, the `Section` wrapper's top padding (pt: 144px, sized for
                // header + country bar) leaves ~20px too much: pull the content up by that amount.
                // Reserve the gap only when a bar is either already there or known to be coming, so a
                // mono-country workspace settles without shifting the content at all.
                mt={countriesData.length > 1 || (expectsCountryBar && isLoadingCountries) ? '24px' : '-20px'}
            >
                    {/* The card frame and its loading skeleton both live inside the grid component,
                        so swapping one for the other never unmounts it (and never resets its
                        columns, sort or pagination). */}
                    <DataGridBrandInfringement
                        showSkeleton={showTableSkeleton}
                        rows={rows}
                        loading={isFetching}
                        onTermClick={handleTermClick}
                        onTermsCellClick={handleTermsCellClick}
                        onDownloadDomainReport={handleDownloadDomainReport}
                        onExportAll={() => handleExportCsv()}
                        rightSlot={
                            <Select
                                size="small"
                                value={days}
                                onChange={(event: SelectChangeEvent<number>) =>
                                    setDays(Number(event.target.value) as Days)
                                }
                                MenuProps={{ PaperProps: { sx: PERIOD_MENU_PAPER_SX } }}
                                renderValue={(value) => (
                                    <Chip
                                        size="small"
                                        label={DAYS_OPTIONS.find((option) => option.value === value)?.label}
                                        sx={PERIOD_CHIP_SX}
                                    />
                                )}
                                sx={{
                                    minWidth: 160,
                                    height: 32,
                                    // Design QA: 20px outside the dropdown, on top of the toolbar's gutter.
                                    mr: '20px',
                                    '& .MuiSelect-select': {
                                        fontSize: '0.8125rem',
                                        // MUI's .MuiInputBase-input height rule wins at equal
                                        // specificity, so force the inner box to the 24px pill — the
                                        // 32px control less its 1px border and 3px of breathing room.
                                        minHeight: '24px !important',
                                        height: '24px',
                                        boxSizing: 'border-box',
                                        paddingTop: 0,
                                        paddingBottom: 0,
                                        // Pill against the left edge: the same 4px inset as above
                                        // and below it (MUI's small Select insets 14px). Same
                                        // specificity tie as the height above.
                                        paddingLeft: '4px !important',
                                        display: 'flex',
                                        alignItems: 'center',
                                    },
                                }}
                            >
                                {DAYS_OPTIONS.map((option) => (
                                    <MenuItem key={option.value} value={option.value} sx={PERIOD_OPTION_SX}>
                                        {option.label}
                                        {option.value === days && <Iconify icon="eva:checkmark-fill" width={12} />}
                                    </MenuItem>
                                ))}
                            </Select>
                        }
                        busyDomain={busyDomain}
                    />
            </Box>
        </Box>
    );
}
